<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {ArrowNarrowLeft, ArrowNarrowRight} from 'utruckin-icons'
import type {IPagination} from '@/features/pagination/pagination.types'
import Dropdown from '@/features/pagination/ui/dropdown.vue'

const props = withDefaults(defineProps<IPagination>(), {})

const emit = defineEmits<{
  (e: 'change-page', page: number): void
  (e: 'change-limit', limit: number): void
}>()

const limits = computed(() => [
  {name: '10', value: 10},
  {name: '20', value: 20},
  {name: '30', value: 30},
  {name: '40', value: 40},
  {name: '50', value: 50},
  {name: '100', value: 100},
  {name: '200', value: 200},
])

const visibleCount = 7
const limit = ref<number>(props.meta?.page_size || 50)

watch(
    () => props.meta?.page_size,
    (newValue) => {
      if (newValue && newValue !== limit.value) {
        limit.value = newValue
      }
    },
    {immediate: true}
)

watch(limit, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emit('change-limit', Number(newValue))
  }
})

const currentPage = computed(() => props.meta?.page || 1)
const totalPages = computed(() => props.meta?.pages || 0)
const hasPrev = computed(() => currentPage.value > 1)
const hasNext = computed(() => currentPage.value < totalPages.value)

const pages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value

  if (total <= visibleCount) {
    return Array.from({length: total}, (_, i) => i + 1)
  }

  const half = Math.floor(visibleCount / 2)
  let start = current - half
  let end = current + half

  if (visibleCount % 2 === 0) {
    end -= 1
  }

  if (start < 1) {
    start = 1
    end = visibleCount
  }

  if (end > total) {
    end = total
    start = total - visibleCount + 1
  }

  return Array.from({length: end - start + 1}, (_, i) => start + i)
})

function goTo(page: number) {
  if (page < 1 || page > totalPages.value) return
  if (page === currentPage.value) return
  emit('change-page', page)
}
</script>

<template>
  <div v-if="totalPages > 0" class="c-co-pagination">
    <div class="limit">
      <dropdown :items="limits" v-model="limit"/>
    </div>

    <div class="pagination">
      <span v-if="pages[0] > 1">
        <button @click="goTo(1)">1</button>
        <span v-if="pages[0] > 2"
              style="display: inline-block; margin-left: 16px; color: var(--color-secondary-label)">...</span>
      </span>

      <button
          v-for="page in pages"
          :key="page"
          :class="{ active: page === currentPage }"
          @click="goTo(page)"
      >
        {{ page }}
      </button>

      <span v-if="pages[pages.length - 1] < totalPages">
        <span v-if="pages[pages.length - 1] < totalPages - 1"
              style="display: inline-block; margin-right: 16px; color: var(--color-secondary-label)">...</span>
        <button @click="goTo(totalPages)">
          {{ totalPages }}
        </button>
      </span>

    </div>

    <div class="rest__info">
      <div class="rest__info-page">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div class="rest__info-actions">
        <button
            class="prev"
            :disabled="!meta.prev_page"
            @click="goTo(currentPage - 1)"
        >
          <ArrowNarrowLeft :size="20"/>
        </button>

        <button
            class="next"
            :disabled="!meta.has_more"
            @click="goTo(currentPage + 1)"
        >
          <ArrowNarrowRight :size="20"/>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-co-pagination {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
  padding: 6px 16px;

  .limit {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .rest__info {
    display: flex;
    align-items: center;

    &-page {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      padding-right: 16px;
      border-right: 1px solid var(--color-border);
      color: var(--color-label);
    }

    &-actions {
      padding-left: 16px;
      display: flex;
      align-items: center;
      gap: 16px;

      button {
        border: none;
        width: 20px;
        height: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--color-label);
        background: var(--color-background);
        cursor: pointer;

        &:disabled {
          cursor: not-allowed;
        }
      }
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    gap: 16px;

    button {
      border: none;
      background: white;
      cursor: pointer;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      background: var(--color-background);
      color: var(--color-secondary-label);

      &.active {
        color: var(--color-label);
      }
    }
  }
}
</style>